// ============================================================================
// GRENADE ENTITY
// ============================================================================

import { EntityId, Vector2 } from '@/shared/types';
import { Vec2, generateId } from '@/shared/utils';
import { CollisionBody, ColliderType, CollisionLayers, CircleCollider } from '@/engine/CollisionSystem';

export interface GrenadeState {
  id: EntityId;
  ownerId: EntityId;
  position: Vector2;
  velocity: Vector2;
  rotation: number;
  fuseTime: number; // Time until explosion
  active: boolean;
  exploded: boolean;
}

export class Grenade {
  public readonly id: EntityId;
  public readonly ownerId: EntityId; // Player who thrown it
  public position: Vector2;
  public velocity: Vector2;
  public rotation: number;
  public rotationSpeed: number;
  public fuseTime: number; // Time remaining until explosion (ms)
  public active: boolean;
  public exploded: boolean;
  public bounceCount: number;
  
  // Top-down physics simulation
  private readonly AIR_RESISTANCE = 0.995; // Velocity decay per frame (air drag)
  private readonly GROUND_FRICTION = 0.92; // Friction when rolling on ground
  private readonly BOUNCE_DAMPING = 0.5; // Energy retained on bounce
  private readonly FUSE_DURATION = 3000; // 3 seconds until explosion
  private readonly FLIGHT_TIME = 800; // Time grenade stays "in air" before landing (ms)
  
  private flightTimer: number; // Tracks if grenade is still in flight
  private isAirborne: boolean;

  constructor(
    ownerId: EntityId,
    position: Vector2,
    throwDirection: Vector2,
    throwPower: number
  ) {
    this.id = generateId('grenade');
    this.ownerId = ownerId;
    this.position = { ...position };
    this.velocity = Vec2.mul(throwDirection, throwPower);
    this.rotation = 0;
    this.rotationSpeed = (Math.random() - 0.5) * 0.02;
    this.fuseTime = this.FUSE_DURATION;
    this.active = true;
    this.exploded = false;
    this.bounceCount = 0;
    this.flightTimer = this.FLIGHT_TIME;
    this.isAirborne = true;
  }

  update(dt: number, _time: number, mapWidth: number, mapHeight: number): boolean {
    if (!this.active || this.exploded) return false;

    // Decrease fuse time
    this.fuseTime -= dt;
    if (this.fuseTime <= 0) {
      this.exploded = true;
      this.active = false;
      return true; // Signal explosion
    }

    // Update flight state - grenade lands after FLIGHT_TIME
    if (this.isAirborne) {
      this.flightTimer -= dt;
      if (this.flightTimer <= 0) {
        this.isAirborne = false;
        // Apply a bounce effect when landing
        const speed = Vec2.length(this.velocity);
        if (speed > 0.1) {
          this.velocity = Vec2.mul(this.velocity, this.BOUNCE_DAMPING);
          this.bounceCount++;
        }
      }
    }

    // Update position
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;

    // Update rotation
    this.rotation += this.rotationSpeed * dt;

    // Apply appropriate friction based on state
    const friction = this.isAirborne ? this.AIR_RESISTANCE : this.GROUND_FRICTION;
    this.velocity.x *= friction;
    this.velocity.y *= friction;

    // Bounce off walls (simplified - just map bounds)
    const margin = 50;
    if (this.position.x < margin) {
      this.position.x = margin;
      this.velocity.x = Math.abs(this.velocity.x) * this.BOUNCE_DAMPING;
      this.bounceCount++;
    } else if (this.position.x > mapWidth - margin) {
      this.position.x = mapWidth - margin;
      this.velocity.x = -Math.abs(this.velocity.x) * this.BOUNCE_DAMPING;
      this.bounceCount++;
    }

    if (this.position.y < margin) {
      this.position.y = margin;
      this.velocity.y = Math.abs(this.velocity.y) * this.BOUNCE_DAMPING;
      this.bounceCount++;
    } else if (this.position.y > mapHeight - margin) {
      this.position.y = mapHeight - margin;
      this.velocity.y = -Math.abs(this.velocity.y) * this.BOUNCE_DAMPING;
      this.bounceCount++;
    }

    // Stop moving if very slow
    if (Math.abs(this.velocity.x) < 0.01 && Math.abs(this.velocity.y) < 0.01) {
      this.velocity.x = 0;
      this.velocity.y = 0;
    }

    return false; // No explosion yet
  }

  getCollisionBody(): CollisionBody {
    const collider: CircleCollider = {
      type: ColliderType.Circle,
      offset: { x: 0, y: 0 },
      radius: 8,
    };
    
    return {
      id: this.id,
      position: this.position,
      velocity: this.velocity,
      collider,
      layer: CollisionLayers.PROJECTILE,
      mask: CollisionLayers.WALL,
      isStatic: false,
      isTrigger: false,
      userData: this,
    };
  }

  toState(): GrenadeState {
    return {
      id: this.id,
      ownerId: this.ownerId,
      position: this.position,
      velocity: this.velocity,
      rotation: this.rotation,
      fuseTime: this.fuseTime,
      active: this.active,
      exploded: this.exploded,
    };
  }

  static fromState(state: GrenadeState): Grenade {
    const grenade = new Grenade(
      state.ownerId,
      state.position,
      Vec2.normalize(state.velocity),
      Vec2.length(state.velocity)
    );
    grenade.rotation = state.rotation;
    grenade.fuseTime = state.fuseTime;
    grenade.active = state.active;
    grenade.exploded = state.exploded;
    return grenade;
  }
}
